import { useEffect, useMemo, useState } from 'react'
import Color from 'color'
import { RgbaColorPicker } from 'react-colorful'
import { omit } from 'midash'
import { cn } from '../utils/cn'
import { useDebouncedCallback } from 'use-debounce'

import { getColor, isValidColor } from '../utils/color'

type View = 'RGBA' | 'CMYK' | 'HEX' | 'HSLA'

function ColorFields ({ view, color, onChange }: { view: View, color: Color, onChange: (c: Color) => void }) {
  const [hex, setHex] = useState(color.hexa())
  const [hexIsFocus, setHexIsFocus] = useState(false)

  useEffect(() => {
    if (!hexIsFocus) {
      setHex(color.hexa())
    }
  }, [color, hexIsFocus])

  const colorFn = {
    'CMYK': color.cmyk.bind(color),
    'RGBA': color.rgb.bind(color),
    'HSLA': color.hsl.bind(color),
    // HEX 并无工作
    'HEX': color.rgb.bind(color)
  }[view]

  const model = {
    'CMYK': 'cmyk',
    'RGBA': 'rgb',
    'HSLA': 'hsl',
    // HEX 并无工作
    'HEX': 'hex'
  }[view]

  const array = useMemo(() => {
    if (view === 'CMYK') {
      return color.cmyk().round().array()
    }
    let arr = colorFn().round().array()
    arr[3] = arr[3] ?? 1
    return arr
  }, [view, color])
  
  if (view === 'HEX') {
    return (
      <div className="">
        <input type="text"
          className="py-1 mb-1 app-input"
          value={hex}
          onBlur={() => {
            setHexIsFocus(false) 
          }}
          onFocus={() => {
            setHexIsFocus(true) 
          }}
          onChange={(e) => {
            const c = e.target.value
            setHex(c)
            try {
              onChange(Color(c))
            } catch (e) {}
          }}
        />
        <div className="text-center text-gray-600">HEX</div>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-4 gap-2 w-[220px]">
      {
        view.split('').map((x, i) => (
          <div key={x}>
            <input
              type="text"
              value={array?.[i] || 0}
              className="px-2 py-1 mb-1 text-center app-input"
              onChange={(e) => {
                const v = e.target.value
                array[i] = Number(v)
                onChange(Color(array, model as any))
              }}
            />
            <div className="text-center text-gray-600">{x}</div>
          </div>
        ))
      }
    </div>
  )
}

function ColorPicker ({ onChange, color, noBackground = false, debounceTime = 200 }: {
  color: Color,
  onChange: (c: Color) => void
  noBackground?: boolean
  debounceTime?: number
}) {
  const [view, setView] = useState<View>('RGBA')

  // 默认为 tailwindcss 的 orange.400
  const rgba = color.rgb().object()

  const style = noBackground ? {} : {
    backgroundColor: color.hexa()
  }

  // 使用 useDebouncedCallback 来创建防抖的回调函数
  const debouncedOnChange = useDebouncedCallback(
    (newColor: Color) => {
      onChange(newColor)
    },
    debounceTime
  )

  return (
    <div className="grid place-content-center w-full" style={style}>
      <div className={cn('p-3 bg-white border rounded shadow', { 'translate-y-8': !noBackground })}>
        <RgbaColorPicker
          className="mb-3 !w-[220px]"
          color={{
            r: rgba.r,
            g: rgba.g,
            b: rgba.b,
            a: rgba.alpha ?? 1
          }}
          onChange={(rgba) => { debouncedOnChange(Color(omit(rgba, ['a'])).alpha(rgba.a)) }}
        />
        <div className="pt-3 text-sm border-t border-gray-400">
          <select className="mb-3 bg-gray-200 border-none app-input" value={view} onChange={e => setView(e.target.value as View)}>
            <option value="RGBA">RGBA</option>
            <option value="CMYK">CMYK</option>
            <option value="HEX">HEX</option>
            <option value="HSLA">HSLA</option>
          </select>
          <ColorFields
            view={view}
            color={color}
            onChange={debouncedOnChange}
          />
        </div>
      </div>
    </div>
  )
}

export default ColorPicker
